import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, CalendarDays, Utensils, KeyRound, Sparkles } from "lucide-react";
import { useEventContent } from "@/context/EventContentContext";
import BrandMark from "@/components/BrandMark";

const HERO_IMG =
  "https://images.unsplash.com/photo-1528605248644-14dd04022da1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwyfHxvdXRkb29yJTIwZm9vZCUyMGZlc3RpdmFsJTIwZWxlZ2FudCUyMGRpbmluZyUyMGF0bW9zcGhlcmV8ZW58MHx8fHwxNzgyNTAxODAwfDA&ixlib=rb-4.1.0&q=85";
const SECONDARY_IMG =
  "https://images.unsplash.com/photo-1635702786344-f09eff66e7c4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwzfHxvdXRkb29yJTIwZm9vZCUyMGZlc3RpdmFsJTIwZWxlZ2FudCUyMGRpbmluZyUyMGF0bW9zcGhlcmV8ZW58MHx8fHwxNzgyNTAxODAwfDA&ixlib=rb-4.1.0&q=85";

const quickLinks = [
  { to: "/evento", label: "Información del evento", icon: Sparkles },
  { to: "/menu", label: "Menú", icon: Utensils },
  { to: "/ubicacion", label: "Ubicación", icon: MapPin },
  { to: "/staff", label: "Staff", icon: KeyRound },
];

export default function Home() {
  const { event } = useEventContent();

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={event.heroImage || HERO_IMG} alt="Atmósfera del evento" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1A2111]/55 via-[#1A2111]/35 to-[#1A2111]/85" />
        </div>
        <div className="relative container-cg min-h-[88vh] flex flex-col justify-end py-24 md:py-32 text-[#FAFAF7]">
          <div className="flex items-center gap-3 overline opacity-90">
            <span className="inline-block w-8 h-px bg-[var(--brand-accent)]" />
            {event.edition}
          </div>
          <h1 className="mt-4">
            <BrandMark
              event={event}
              imageClassName="max-h-28 sm:max-h-36 max-w-[80vw] sm:max-w-xl"
              textClassName="font-serif-cg block text-5xl sm:text-6xl lg:text-8xl font-light tracking-tight leading-[0.95]"
            />
          </h1>
          <p className="font-serif-cg text-xl md:text-2xl mt-4 opacity-95">{event.subtitle}</p>
          <p className="max-w-2xl mt-5 text-sm md:text-base opacity-90 leading-relaxed">
            {event.tagline}
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/evento" data-testid="hero-cta-info" className="btn-primary-cg">
              Información del evento <ArrowUpRight size={16} />
            </Link>
            <Link to="/menu" data-testid="hero-cta-menu" className="btn-secondary-cg" style={{ color: "#FAFAF7", borderColor: "#FAFAF7" }}>
              Menú
            </Link>
            <Link to="/ubicacion" data-testid="hero-cta-location" className="btn-secondary-cg" style={{ color: "#FAFAF7", borderColor: "#FAFAF7" }}>
              Ubicación
            </Link>
            <Link to="/staff" data-testid="hero-cta-staff" className="btn-secondary-cg" style={{ color: "#FAFAF7", borderColor: "#FAFAF7" }}>
              Staff
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Facts */}
      <section className="border-b border-[var(--brand-border)]">
        <div className="container-cg grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--brand-border)]">
          {[
            { k: "Fecha", v: event.date },
            { k: "Horario", v: event.time },
            { k: "Sede", v: event.venue },
            { k: "Ciudad", v: event.city },
          ].map((it) => (
            <div key={it.k} className="bg-[var(--brand-bg)] p-6 md:p-8">
              <div className="overline text-[var(--brand-muted)]">{it.k}</div>
              <div className="font-serif-cg text-lg md:text-xl mt-2 leading-snug">{it.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* About teaser */}
      <section className="section-pad">
        <div className="container-cg grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <div className="overline text-[var(--brand-primary)]">Sobre la edición</div>
            <h2 className="font-serif-cg text-4xl md:text-5xl mt-3 tracking-tight">
              {event.aboutTitle || "Leon cumple 450 anos, y lo celebramos con una cuchara grande."}
            </h2>
          </div>
          <div className="md:col-span-7 md:pl-8">
            <p className="text-base md:text-lg leading-relaxed text-[var(--brand-ink)]/85">
              {event.aboutText ||
                "Cuchara Grande es una experiencia gastronomica que reune a chefs, productores y aliados educativos que han construido la identidad culinaria del Bajio."}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/evento" className="btn-primary-cg" data-testid="home-about-link">
                Conoce más <ArrowUpRight size={16} />
              </Link>
              <Link to="/chefs" className="btn-secondary-cg">
                Ver chefs invitados
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick links grid */}
      <section className="section-pad bg-[var(--brand-surface-alt)]">
        <div className="container-cg">
          <div className="overline text-[var(--brand-primary)] mb-2">Accesos rápidos</div>
          <h3 className="font-serif-cg text-3xl md:text-4xl tracking-tight mb-10">Explora el festival</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {quickLinks.map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                data-testid={`quick-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className="card-cg p-6 group flex flex-col justify-between min-h-[160px]"
              >
                <Icon size={22} className="text-[var(--brand-primary)]" />
                <div className="flex items-end justify-between mt-8">
                  <span className="font-serif-cg text-lg md:text-xl leading-tight">{label}</span>
                  <ArrowUpRight size={18} className="text-[var(--brand-muted)] group-hover:text-[var(--brand-primary)] transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* León 450 monumental section */}
      <section className="section-pad relative overflow-hidden">
        <div className="container-cg grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="overline text-[var(--brand-accent)]">Edición conmemorativa</div>
            <h2 className="font-serif-cg text-5xl sm:text-7xl md:text-8xl tracking-tight mt-3 leading-[0.95]">
              León <span className="italic">450</span>
            </h2>
            <p className="mt-6 text-lg leading-relaxed max-w-xl text-[var(--brand-ink)]/85">
              {event.commemorativeText ||
                "Esta edicion celebra la historia, la creatividad y la riqueza culinaria de Leon como parte de los festejos por sus 450 anos."}
            </p>
          </div>
          <div className="md:col-span-5">
            <div className="card-cg overflow-hidden img-zoom-host">
              <img
                src={event.secondaryImage || SECONDARY_IMG}
                alt="Ambiente del evento"
                className="img-zoom w-full aspect-[4/5] object-cover"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
